"use client";

import { useState } from "react";
import type { GameReview } from "@/lib/chess/review";
import { moveLabel } from "@/lib/chess/study";
import { advantageLabel } from "@/lib/chess/reviewSummary";
import { PlayIcon } from "./PlayIcon";

export function AdvantageChart({ review, playerColor, onSelect }: { review: GameReview; playerColor: "w" | "b"; onSelect: (ply: number) => void }) {
  const [position, setPosition] = useState(0);
  const curve = review.curve.map((value) => playerColor === "w" ? value : 100 - value);
  const current = Math.min(position, curve.length - 1);
  const x = (index: number) => 12 + index / Math.max(1, curve.length - 1) * 776;
  const y = (value: number) => 16 + (100 - value) * 1.48;
  const path = curve.map((value, index) => `${index ? "L" : "M"}${x(index)},${y(value)}`).join(" ");
  const label = current ? `After ${moveLabel(review.moves[current - 1])}` : "Starting position";
  const description = advantageLabel(curve[current] ?? 50);
  return <div className="review-chart">
    <div className="review-chart-labels"><span>Better for you</span><span>Balanced</span><span>Better for opponent</span></div>
    <div className="review-chart-plot">
      <svg viewBox="0 0 800 180" preserveAspectRatio="none" role="img" aria-label="How the advantage changed. Higher means a better position for you. Use the slider below to explore.">
        <path d={`${path} L${x(curve.length - 1)},90 L12,90 Z`} fill="var(--accent)" opacity=".12" />
        {[16, 90, 164].map((height) => <line key={height} x1="12" x2="788" y1={height} y2={height} stroke="var(--border)" strokeDasharray={height === 90 ? "5 5" : undefined} />)}
        <path d={path} stroke="var(--accent)" strokeWidth="3" vectorEffect="non-scaling-stroke" fill="none" strokeLinejoin="round" />
        <line x1={x(current)} x2={x(current)} y1="12" y2="168" stroke="var(--text)" opacity=".5" />
        {review.moves.filter((move) => move.color === playerColor && ["mistake", "blunder"].includes(move.classification)).map((move) => <circle key={move.ply} cx={x(move.ply)} cy={y(curve[move.ply])} r="4" fill="var(--review-caution)" />)}
        <circle cx={x(current)} cy={y(curve[current] ?? 50)} r="5" fill="var(--text)" />
      </svg>
      <input type="range" min={0} max={Math.max(0, curve.length - 1)} value={current} aria-label="Explore the game's advantage" aria-valuetext={`${label}. ${description}.`} onChange={(event) => setPosition(Number(event.target.value))} />
      <div className="review-chart-endpoints"><span>Start</span><span>End of game</span></div>
    </div>
    <div className="review-chart-readout"><span><strong>{label}</strong><span>{description}</span></span><button className="club-text-button" onClick={() => onSelect(current)}>View position <PlayIcon name="arrow" /></button></div>
  </div>;
}
